/* eslint-disable no-empty */
import useLocale from '@/hooks/useLocale';
import { useCallback, useState, useRef } from 'react';
import yaml from 'js-yaml';
import { COUNTRY, config } from '@/config';
import { useHistory } from 'react-router';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { isBRServer } from '@/utils/Validators/validation';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  NEW_SUPPORT,
  CHARLIE_UPGRADE_SITEBUILDER_FORM,
  ATIVACAO_CHANGE_DOMAIN_FLOW,
  ATIVACAO_SKIP_TICKET_TYPE_SELECTION,
} from '@/config/GrowthBook/constants';

const useSupportForms = ({
  defaultLoading = true,
  defaultProductsOptions = [],
  defaultFormsValue = [],
  defaultSelectedProductOption = '',
  defaultProductsArray = [],
  defaultSelectedNeed = null,
  defaultTechnicalOptions = [],
  defaultBillingOptions = [],
  defaultSelectedSector = '',
  defaultselectedProductData = null,
  forceState,
  rules,
}) => {
  const history = useHistory();
  const generalDoubtsV2 = !isBRServer;
  const upgradeSitebuilder = useFeatureIsOn(CHARLIE_UPGRADE_SITEBUILDER_FORM);
  const changeDomainFlow = useFeatureIsOn(ATIVACAO_CHANGE_DOMAIN_FLOW);
  const removeCommonForms = useFeatureIsOn(ATIVACAO_SKIP_TICKET_TYPE_SELECTION);
  const useNewSupport = useFeatureIsOn(NEW_SUPPORT);
  const { ticket: ticketsLocale, routes: routesLocale } = useLocale();
  const [billingOptions, setBillingOptions] = useState(defaultBillingOptions);
  const [technicalOptions, setTechnicalOptions] = useState(defaultTechnicalOptions);
  const [products, setProducts] = useState(defaultProductsArray);
  const [defaultForms, setDefaultForms] = useState(defaultFormsValue);
  const [productsOptions, setProductsOptions] = useState(defaultProductsOptions);
  const [loading, setLoading] = useState(defaultLoading);
  const [selectedProductOption, setSelectedProductOption] = useState(defaultSelectedProductOption);
  const [selectedProductFormmd, setSelectedProductFormmd] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductData, setSelectedProductData] = useState(defaultselectedProductData);
  const [selectedSector, setSelectedSector] = useState(defaultSelectedSector);
  const [selectedNeed, setSelectedNeed] = useState(defaultSelectedNeed);
  const [needsOptions, setNeedsOptions] = useState(null);
  const [activeProductsOptions, setActiveProductsOptions] = useState([]);

  const prevSelectedSectorRef = useRef(selectedSector);

  const accountFormsV2 = useCallback(() => {
    const formsArray = [{
      label: ticketsLocale.forms.names.withoutEmailAccess,
      id: ticketsLocale.forms.names.withoutEmailAccess,
      key: ticketsLocale.forms.names.withoutEmailAccess,
      v2: true,
    }];

    formsArray.push({
      label: ticketsLocale.forms.names.affiliateProgram,
      id: ticketsLocale.forms.names.affiliateProgram,
      key: ticketsLocale.forms.names.affiliateProgram,
      v2: true,
    });

    isBRServer && formsArray.push({
      label: ticketsLocale.forms.names.nationalDomainTransfer,
      id: ticketsLocale.forms.names.nationalDomainTransfer,
      key: ticketsLocale.forms.names.nationalDomainTransfer,
      v2: true,
    });

    return formsArray;
  }, [ticketsLocale]);

  const technicalFormsV2 = useCallback(() => {
    const formsArray = [{
      label: ticketsLocale.forms.names.internalMigration,
      id: ticketsLocale.forms.names.internalMigration,
      key: ticketsLocale.forms.names.internalMigration,
      v2: true,
    }];

    formsArray.push({
      label: ticketsLocale.forms.names.restorateBackupHgRoutine,
      id: ticketsLocale.forms.names.restorateBackupHgRoutine,
      key: ticketsLocale.forms.names.restorateBackupHgRoutine,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.userBackupRestore,
      id: ticketsLocale.forms.names.userBackupRestore,
      key: ticketsLocale.forms.names.userBackupRestore,
      v2: true,
    });

    if (forceState && rules.generateBackup && rules.generateBackup.allowBackup) {
      formsArray.push({
        label: ticketsLocale.forms.names.generateBackup,
        id: ticketsLocale.forms.names.generateBackup,
        key: ticketsLocale.forms.names.generateBackup,
        v2: true,
      });
    }

    if (!changeDomainFlow) {
      formsArray.push({
        label: ticketsLocale.forms.names.changePrimaryDomain,
        id: ticketsLocale.forms.names.changePrimaryDomain,
        key: ticketsLocale.forms.names.changePrimaryDomain,
        v2: true,
      });
    }

    formsArray.push({
      label: ticketsLocale.forms.names.externalMigration,
      id: ticketsLocale.forms.names.externalMigration,
      key: ticketsLocale.forms.names.externalMigration,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.googleAdwordsCoupon,
      id: ticketsLocale.forms.names.googleAdwordsCoupon,
      key: ticketsLocale.forms.names.googleAdwordsCoupon,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.installExternalSsl,
      id: ticketsLocale.forms.names.installExternalSsl,
      key: ticketsLocale.forms.names.installExternalSsl,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.restorateCancelledPlan,
      id: ticketsLocale.forms.names.restorateCancelledPlan,
      key: ticketsLocale.forms.names.restorateCancelledPlan,
      v2: true,
    });

    isBRServer && formsArray.push({
      label: ticketsLocale.forms.names.generateCSR,
      id: ticketsLocale.forms.names.generateCSR,
      key: ticketsLocale.forms.names.generateCSR,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.restorateSiteBuilderAccess,
      id: ticketsLocale.forms.names.restorateSiteBuilderAccess,
      key: ticketsLocale.forms.names.restorateSiteBuilderAccess,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.privateSslCertificate,
      id: ticketsLocale.forms.names.privateSslCertificate,
      key: ticketsLocale.forms.names.privateSslCertificate,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.widlcardSslCertificate,
      id: ticketsLocale.forms.names.widlcardSslCertificate,
      key: ticketsLocale.forms.names.widlcardSslCertificate,
      v2: true,
    });

    isBRServer && formsArray.push({
      label: ticketsLocale.forms.names.enterPrisessslCertificate,
      id: ticketsLocale.forms.names.enterPrisessslCertificate,
      key: ticketsLocale.forms.names.enterPrisessslCertificate,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.installWhmcs,
      id: ticketsLocale.forms.names.installWhmcs,
      key: ticketsLocale.forms.names.installWhmcs,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.reloadRebuild,
      id: ticketsLocale.forms.names.reloadRebuild,
      key: ticketsLocale.forms.names.reloadRebuild,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.reloadRebuildVpsOci,
      id: ticketsLocale.forms.names.reloadRebuildVpsOci,
      key: ticketsLocale.forms.names.reloadRebuildVpsOci,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.moreEmails,
      id: ticketsLocale.forms.names.moreEmails,
      key: ticketsLocale.forms.names.moreEmails,
      v2: true,
    });

    isBRServer && formsArray.push({
      label: ticketsLocale.forms.names.migrateVpsDedicated,
      id: ticketsLocale.forms.names.migrateVpsDedicated,
      key: ticketsLocale.forms.names.migrateVpsDedicated,
      v2: true,
    });

    !isBRServer && formsArray.push({
      label: ticketsLocale.forms.names.migrateAccountVpsDedicated,
      id: ticketsLocale.forms.names.migrateAccountVpsDedicated,
      key: ticketsLocale.forms.names.migrateAccountVpsDedicated,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.updateWhmcsLicense,
      id: ticketsLocale.forms.names.updateWhmcsLicense,
      key: ticketsLocale.forms.names.updateWhmcsLicense,
      v2: true,
    });

    if (upgradeSitebuilder && !isBRServer) {
      formsArray.push({
        label: ticketsLocale.forms.names.upgradeSiteBuilder,
        id: ticketsLocale.forms.names.upgradeSiteBuilder,
        key: ticketsLocale.forms.names.upgradeSiteBuilder,
        v2: true,
      });
    }

    if (!isBRServer) {
      formsArray.push({
        label: ticketsLocale.forms.names.sslEV,
        id: ticketsLocale.forms.names.sslEV,
        key: ticketsLocale.forms.names.sslEV,
        v2: true,
      });
    }

    formsArray.push({
      label: ticketsLocale.forms.names.whmcsAcquisition,
      id: ticketsLocale.forms.names.whmcsAcquisition,
      key: ticketsLocale.forms.names.whmcsAcquisition,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.migrateReseller,
      id: ticketsLocale.forms.names.migrateReseller,
      key: ticketsLocale.forms.names.migrateReseller,
      v2: true,
    });

    return formsArray;
  }, [
    ticketsLocale,
    upgradeSitebuilder,
    forceState,
    rules,
    changeDomainFlow,
  ]);

  const billingFormsV2 = useCallback(() => {
    const formsArray = [];

    if (generalDoubtsV2) {
      formsArray.push({
        label: ticketsLocale.forms.names.financialDoubts,
        id: ticketsLocale.forms.names.financialDoubts,
        key: ticketsLocale.forms.names.financialDoubts,
        v2: true,
      });
    }

    const productPid = (selectedProductData && !!selectedProductData.pid && selectedProductData.pid)
      || (selectedProduct && selectedProduct.pid);

    if (HgProducts && HgProducts.DOMAINS_ALL && !HgProducts.DOMAINS_ALL.includes(productPid)) {
      formsArray.push({
        label: ticketsLocale.forms.names.changeDueDate,
        id: ticketsLocale.forms.names.changeDueDate,
        key: ticketsLocale.forms.names.changeDueDate,
        v2: true,
      });
    }

    formsArray.push({
      label: ticketsLocale.forms.names.upgradeVps,
      id: ticketsLocale.forms.names.upgradeVps,
      key: ticketsLocale.forms.names.upgradeVps,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.changePlanVpsOci,
      id: ticketsLocale.forms.names.changePlanVpsOci,
      key: ticketsLocale.forms.names.changePlanVpsOci,
      v2: true,
    });

    if (isBRServer) {
      formsArray.push({
        label: ticketsLocale.forms.names.upgradeSiteLock,
        id: ticketsLocale.forms.names.upgradeSiteLock,
        key: ticketsLocale.forms.names.upgradeSiteLock,
        v2: true,
      });
    }


    formsArray.push({
      label: ticketsLocale.forms.names.upgradeCodeGuard,
      id: ticketsLocale.forms.names.upgradeCodeGuard,
      key: ticketsLocale.forms.names.upgradeCodeGuard,
      v2: true,
    });

    formsArray.push({
      label: ticketsLocale.forms.names.ChangePaymentcycle,
      id: ticketsLocale.forms.names.ChangePaymentcycle,
      key: ticketsLocale.forms.names.ChangePaymentcycle,
      v2: true,
    });

    return formsArray;
  }, [ticketsLocale, generalDoubtsV2, selectedProductData, selectedProduct]);


  const validate = useCallback(({ selectedProductData, rule }) => {
    const { method } = rule;
    let groups = null;
    let ruleProducts = null;
    let isOk = false;

    if (rule.or) {
      const ruleResult = rule.or.some(rule => validate({ selectedProductData, rule })); // recursive call
      return ruleResult;
    }

    if (rule.and) {
      const ruleResult = rule.and.every(rule => validate({ selectedProductData, rule })); // recursive call
      return ruleResult;
    }

    switch (method) {
      case 'isInGroup':
        groups = rule.vars.map(r => HgProducts[r]);
        if (groups) {
          groups.forEach((group) => {
            if (group && group.includes(selectedProductData.pid)) {
              isOk = true;
            }
          });
        }
        return isOk;
      case 'isNotInGroup':
        groups = rule.vars.map(r => HgProducts[r]);
        if (groups) {
          groups.forEach((group) => {
            if (!group.includes(selectedProductData.pid)) {
              isOk = true;
            }
          });
        }
        return isOk;
      case 'isProduct':
        if (Array.isArray(rule.vars)) {
          ruleProducts = rule.vars.map(r => HgProducts[r]);
          return ruleProducts.includes(selectedProductData.pid);
        }
        return rule.vars === selectedProductData.pid;
      case 'notIsProduct':
        if (Array.isArray(rule.vars)) {
          ruleProducts = rule.vars.map(r => HgProducts[r]);
          return !ruleProducts.includes(selectedProductData.pid);
        }
        return rule.vars !== selectedProductData.pid;
      case 'eq':
        if (rule.vars[0].pop() === 'brand') {
          switch (rule.vars[1]) {
            case 1:
              return COUNTRY === 'mx';
            case 2:
              return COUNTRY === 'cl';
            case 3:
              return COUNTRY === 'co';
            default:
              return false;
          }
        }
        return false;
      default:
        return false;
    }
  }, []);

  const parseForm = (data) => {
    const splitted = data.split('---');
    const form = yaml.load(splitted[1]);
    return form;
  };

  const requestForms = useCallback((preSelectedCategory) => {
    setLoading(true);
    try {
      fetch(`${config.API_URL}/formconfig_new`, {
        headers: {
          Authorization: localStorage.getItem('token'),
        },
      })
        .then((response) => {
          try {
            const jsonResp = response.json();
            return jsonResp;
          } catch (e) {
            return response;
          }
        })
        .then((formData) => {
          const { products } = formData.data;
          const defaultForms = formData.data.forms;

          const billingForms = [];
          const technicalForms = [];
          const productsOptions = [];
          let commonForms = [];


          technicalFormsV2().forEach(form => technicalForms.push(form));
          billingFormsV2().forEach(form => billingForms.push(form));

          setTechnicalOptions([...technicalForms.sort((a, b) => {
            if (b.label > a.label) {
              return -1;
            }
            return 1;
          })]);

          setBillingOptions([...billingForms.sort((a, b) => {
            if (b.label > a.label) {
              return -1;
            }
            return 1;
          })]);

          const activeProducts = [...products].filter(product => product.active);
          if (activeProducts.length) {
            setActiveProductsOptions(activeProducts);
            productsOptions.push({ value: 'active_products', label: ticketsLocale.openNewTicketPage.selectProductGroups.activeProducts, title: true });
          }
          activeProducts.forEach((product) => {
            productsOptions.push({
              label: `${product.name} - ${product.domain}`,
              // Caso altere o padrão, alterar também em SignatureConfirmation.jsx:37 e ProductDetailPage.js:170 -> getProductNamePackageVPSOci
              value: product.id,
              type: 'product',
            });
          });

          const suspendedProducts = [...products].filter(product => !product.active);
          if (suspendedProducts.length) {
            productsOptions.push({ value: 'suspended_products', label: ticketsLocale.openNewTicketPage.selectProductGroups.suspendedProducts, title: true });
          }
          suspendedProducts.forEach((product) => {
            productsOptions.push({
              label: `${product.name} - ${product.domain}`,
              // Caso altere o padrão, alterar também em SignatureConfirmation.jsx:37 e ProductDetailPage.js:170 -> getProductNamePackageVPSOci
              value: product.id,
              type: 'product',
            });
          });

          productsOptions.push(
            {
              value: 'my_account',
              label: ticketsLocale.openNewTicketPage.selectProductGroups.myAccount,
              title: true,
            },
          );

          accountFormsV2().forEach(form => productsOptions.push(form));

          commonForms = [
            { value: 'common', label: ticketsLocale.openNewTicketPage.selectProductGroups.commonQuestions, title: true },
            ...ticketsLocale.commonForms,
          ];

          if (useNewSupport && preSelectedCategory) {
            const preSelectedCategoryForm = commonForms.find(item => item.value === preSelectedCategory);
            if (preSelectedCategoryForm) {
              setSelectedProductOption(preSelectedCategoryForm.label);
            }
          } else {
            setSelectedProductOption(defaultSelectedProductOption);
          }

          if (useNewSupport && !removeCommonForms) {
            productsOptions.push(...commonForms);
          }

          setProducts(products);
          setDefaultForms(defaultForms);
          setProductsOptions(productsOptions);
          setLoading(false);
        });
    } catch (e) { }
  }, [
    ticketsLocale,
    technicalFormsV2,
    billingFormsV2,
    accountFormsV2,
    useNewSupport,
    removeCommonForms,
    defaultSelectedProductOption,
  ]);

  const checkProduct = useCallback(() => {
    if (productsOptions && selectedProductOption) {
      const option = defaultForms.find(item => item.label === selectedProductOption);

      if (option && option.id) {
        history.push(`${routesLocale.help}${routesLocale.createTicket}/${`${option.key}`}`);
        setSelectedProductFormmd(`${option.key}`);
      }

      if (!option) {
        const productSelectedOption = productsOptions.find(item => item.label === selectedProductOption);
        const product = productSelectedOption && products.find(item => item.id === productSelectedOption.value);

        if (product) {
          setSelectedProduct(product);
          setSelectedProductFormmd(product.id);
          setSelectedProductData(product);
        }
      }
    }
  }, [selectedProductOption, history, routesLocale, productsOptions, defaultForms, products]);

  const checkNeed = useCallback(() => {
    if (billingOptions && technicalOptions && selectedNeed) {
      const formsArray = selectedSector === 'technical' ? [...technicalOptions] : [...billingOptions];
      const option = formsArray.find(item => item.label === selectedNeed);

      if (option && option.id && !option.v2) {
        history.push(`${routesLocale.help}${routesLocale.createTicket}/${selectedProductFormmd}/${selectedSector}/${`${option.key}`}`);
      }
    }
  }, [
    history,
    billingOptions,
    technicalOptions,
    selectedNeed,
    routesLocale,
    selectedProductFormmd,
    selectedSector,
  ]);

  const removeFormsPerPlanAttributes = useCallback(({
    selectedProduct,
    HgProducts,
    formsOptions,
  }) => {
    let newFormsOptions = [...formsOptions];
    const isShared = HgProducts.SHARED_ALL.includes(selectedProduct.pid);
    const isWpHosting = HgProducts.WPHOSTING_ALL.includes(selectedProduct.pid);
    const isReseller = HgProducts.RESELLER_ALL.includes(selectedProduct.pid);
    const isVps = HgProducts.VPS_ALL.includes(selectedProduct.pid);
    const isDedicated = HgProducts.DEDICATED_ALL.includes(selectedProduct.pid);
    const isVpsOCI = HgProducts.VPS_OCI.includes(selectedProduct.pid);
    const shouldRemoveMoreEmailsForms = !isVps && !isDedicated && !isVpsOCI;
    const shouldRemoveMigrateVpsDedicated = !isVps && !isDedicated && !isVpsOCI;
    const isPlus = HgProducts.PLUS_ALL.includes(selectedProduct.pid);
    const isSitelock = HgProducts.SITELOCK_ALL.includes(selectedProduct.pid);
    const isCodeGuard = HgProducts.CODEGUARD_ALL.includes(selectedProduct.pid);
    const isStreaming = HgProducts.STREAMING_ALL.includes(selectedProduct.pid);
    const isWeebly = HgProducts.WEEBLY_ALL.includes(selectedProduct.pid);
    const isSiteBuilderPlan = !isBRServer && [HgProducts.WEB_HOSTING_CREADOR_DE_SITIOS_PERSONAL, HgProducts.WEB_HOSTING_CREADOR_DE_SITIOS_PROFESIONAL].includes(selectedProduct.pid);
    const isSslEvPlan = !isBRServer && [
      HgProducts.WEEBLY_BASICO,
      HgProducts.WEEBLY_INICIANTE,
      HgProducts.WEEBLY_PROFESIONAL,
      HgProducts.RESELLER_ALUMINIO,
      HgProducts.RESELLER_COBRE,
      HgProducts.RESELLER_DIAMANTE,
      HgProducts.RESELLER_ORO,
      HgProducts.RESELLER_PLATA,
      HgProducts.RESELLER_INICIANDO,
      HgProducts.RESELLER_CRECIENDO,
      HgProducts.RESELLER_EXPANDIENDO,
      HgProducts.RESELLER_VETERANO,
      HgProducts.RESELLER_VETERANO_100,
      HgProducts.RESELLER_VETERANO_150,
      HgProducts.RESELLER_VETERANO_200,
      HgProducts.RESELLER_VETERANO_300,
      HgProducts.RESELLER_VETERANO_500,
      HgProducts.RESELLER_VETERANO_1000,
      HgProducts.RESELLER_VETERANO_1200,
      ...HgProducts.DEDICATED_ALL,
      ...HgProducts.VPS_ALL,
    ].includes(selectedProduct.pid);

    const isNewVps = [
      HgProducts.VPS_ENTRY,
      HgProducts.VPS_OPTIMIZED,
      HgProducts.VPS_PLATINUM,
      HgProducts.VPS_STANDARD,
      HgProducts.VPS_2,
      HgProducts.VPS_4,
      HgProducts.VPS_4_PLUS,
      HgProducts.VPS_6,
      HgProducts.VPS_8,
    ].includes(selectedProduct.pid);

    const isDomain = HgProducts.DOMAINS_ALL.includes(selectedProduct.pid);

    const isSitelockEnterprise = HgProducts && HgProducts.SITELOCK_ENTERPRISE === selectedProduct.pid;

    const shouldRemoveChangePrimaryDomain = (!isShared && !isReseller && !isVps && !isDedicated && !isPlus) || isWpHosting;
    const shouldRemoveInternalMigration = !isShared && !isPlus;
    const shouldRemoveExternalMigration = !isShared && !isPlus;
    const shouldRemoveGoogleAdwordsCoupon = !isShared && !isReseller && !isPlus;
    const shouldRemoveRestorateCancelledPlan = !isShared && !isReseller && !isPlus;
    const shouldRemoveRestorateSiteBuilderAccess = !isShared && !isReseller && !isPlus;
    const shouldRemoveSitelockUpgrade = isBRServer && (!isSitelock || isSitelockEnterprise);
    const shouldRemoveCodeGuardUpgrade = !isCodeGuard;
    const shouldRemoveChangeCycle = !isVps && !isWpHosting && !isStreaming && !isDedicated;
    const shouldRemoveEnterpriseSslCertificate = isBRServer && !isReseller && !isWeebly && !isVps && !isDedicated && !isVpsOCI;
    const shouldRemovePrivateSslCertificate = isBRServer
      ? !isVps && !isDedicated && !isVpsOCI && !isReseller
      : !isVps && !isDedicated && !isVpsOCI && !isReseller && !isWeebly;
    const canDisplayVpsUpgrade = HgProducts.NEW_VPS.includes(selectedProduct.pid)
      ? !!HgProducts && selectedProduct.pid !== HgProducts.VPS_8
      : true;
    const shouldRemoveReloadRebuild = true;
    const shouldRemoveReloadRebuildVpsOci = true;
    const shouldRemoveExternalSsl = !isShared
      && !isReseller
      && !isPlus
      && !isVps
      && !isDedicated
      && !isVpsOCI
      && HgProducts.WORDPRESS_PRO !== selectedProduct.pid
      && HgProducts.WORDPRESS_VIP !== selectedProduct.pid;
    const shouldRemoveInstallWhmcs = !isReseller && !isDedicated;
    const shouldRemoveVpsUpgrade = (!isNewVps || isVpsOCI) || !canDisplayVpsUpgrade;
    const shouldRemoveVpsOciChangePlan = !isVpsOCI;
    const shouldRemoveUpdateWhmcs = !isReseller && !isDedicated;
    const shouldRemoveWhmcsAcquisition = !isReseller && !isDedicated;
    const shouldRemoveResellerMigration = !isReseller;
    const shouldRemoveUpgradeSitebuilder = !isSiteBuilderPlan;
    const shouldRemoveSslEv = !isSslEvPlan;
    const shouldRemoveChangedueDate = isDomain;
    const shouldRemoveHgRoutineBackup = isVps || isDedicated || isVpsOCI;
    const shouldRemoveGenerateBackup = isVps || isDedicated || isVpsOCI;


    if (shouldRemoveHgRoutineBackup) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.restorateBackupHgRoutine);
    }

    if (shouldRemoveGenerateBackup) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.generateBackup);
    }

    if (shouldRemoveChangePrimaryDomain) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.changePrimaryDomain);
    }

    if (shouldRemoveInternalMigration) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.internalMigration);
    }

    if (shouldRemoveExternalMigration) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.externalMigration);
    }

    if (shouldRemoveGoogleAdwordsCoupon) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.googleAdwordsCoupon);
    }

    if (shouldRemoveExternalSsl) {
      newFormsOptions = newFormsOptions.filter(item => !(item && item.id && item.id.toString().includes(ticketsLocale.forms.names.installExternalSsl)));
    }

    if (shouldRemoveRestorateCancelledPlan) {
      newFormsOptions = newFormsOptions.filter(item => !(item && item.id && item.id.toString().includes(ticketsLocale.forms.names.restorateCancelledPlan)));
    }

    if (shouldRemoveRestorateSiteBuilderAccess) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.restorateSiteBuilderAccess);
    }

    if (shouldRemoveVpsUpgrade) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.upgradeVps);
    }

    if (shouldRemoveVpsOciChangePlan) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.changePlanVpsOci);
    }

    if (shouldRemoveSitelockUpgrade) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.upgradeSiteLock);
    }

    if (shouldRemoveCodeGuardUpgrade) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.upgradeCodeGuard);
    }

    if (shouldRemoveUpgradeSitebuilder) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.upgradeSiteBuilder);
    }

    if (shouldRemoveChangeCycle) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.ChangePaymentcycle);
    }

    if (shouldRemovePrivateSslCertificate) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.privateSslCertificate);
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.widlcardSslCertificate);
    }

    if (shouldRemoveEnterpriseSslCertificate) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.enterPrisessslCertificate);
    }

    if (shouldRemoveInstallWhmcs) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.installWhmcs);
    }

    if (shouldRemoveReloadRebuild) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.reloadRebuild);
    }

    if (shouldRemoveReloadRebuildVpsOci) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.reloadRebuildVpsOci);
    }

    if (shouldRemoveMoreEmailsForms) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.moreEmails);
    }

    if (shouldRemoveMigrateVpsDedicated) {
      if (isBRServer) {
        newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.migrateVpsDedicated);
      }

      if (!isBRServer) {
        newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.migrateAccountVpsDedicated);
      }
    }

    if (shouldRemoveUpdateWhmcs) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.updateWhmcsLicense);
    }

    if (shouldRemoveWhmcsAcquisition) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.whmcsAcquisition);
    }

    if (shouldRemoveResellerMigration) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.migrateReseller);
    }

    if (shouldRemoveSslEv) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.sslEV);
    }

    if (shouldRemoveChangedueDate) {
      newFormsOptions = newFormsOptions.filter(item => item.id !== ticketsLocale.forms.names.changeDueDate);
    }


    return newFormsOptions;
  }, [
    ticketsLocale,
  ]);

  const checkSector = useCallback(() => {
    const sectorChanged = prevSelectedSectorRef.current !== selectedSector;
    prevSelectedSectorRef.current = selectedSector;

    if (selectedSector) {
      let isOk = false;
      let formsOptions = selectedSector === 'technical' ? [...technicalOptions] : [...billingOptions];
      formsOptions = formsOptions.filter((item) => {
        if (item && item.form && item.form.showWhen === true) {
          isOk = item.form.showWhen;
        }

        if (item && item.form && item.form.showWhen instanceof Array) {
          isOk = item.form.showWhen.some(rule => validate({ selectedProductData, rule }));
        }

        if (item.v2) {
          isOk = true;
        }

        const isResellerOrShared = selectedProduct && (
          HgProducts.SHARED_ALL.includes(selectedProduct.pid)
          || HgProducts.RESELLER_ALL.includes(selectedProduct.pid)
          || HgProducts.VPS_ALL.includes(selectedProduct.pid)
          || HgProducts.DEDICATED_ALL.includes(selectedProduct.pid)
        );

        const isVps = selectedProduct && HgProducts.VPS_ALL.includes(selectedProduct.pid);
        const isVpsOCI = selectedProduct && HgProducts.VPS_OCI.includes(selectedProduct.pid);
        const isDedicated = selectedProduct && HgProducts.DEDICATED_ALL.includes(selectedProduct.pid);

        const backupOptions = [
          ticketsLocale.forms.names.generateBackup,
          ticketsLocale.forms.names.restorateBackupHgRoutine,
          ticketsLocale.forms.names.userBackupRestore,
          54,
        ];

        if ((!isResellerOrShared && !isVps && !isVpsOCI && !isDedicated) && item && backupOptions.includes(item.id)) {
          isOk = false;
        }

        if (selectedProduct && HgProducts.PLUS === selectedProduct.pid && item && item.id === ticketsLocale.forms.names.restorateBackupHgRoutine) {
          isOk = true;
        }

        if ((isVps || isDedicated) && item && item.id === ticketsLocale.forms.names.restorateBackupHgRoutine) {
          isOk = false;
        }

        return isOk;
      });

      if (selectedProduct && HgProducts) {
        formsOptions = removeFormsPerPlanAttributes({ selectedProduct, HgProducts, formsOptions });
      }

      setNeedsOptions(formsOptions);
    }

    if (sectorChanged) {
      setSelectedNeed(null);
    }
  }, [
    billingOptions,
    technicalOptions,
    validate,
    selectedSector,
    selectedProductData,
    ticketsLocale,
    selectedProduct,
    removeFormsPerPlanAttributes,
  ]);

  const handleSelectedProductOption = useCallback((label) => {
    if (productsOptions) {
      setSelectedProductOption(label);
    }
    setSelectedProduct(null);
    setSelectedNeed(null);
    setSelectedSector(null);
  }, [productsOptions]);

  const useSupportFormsData = {
    requestForms,
    productsOptions,
    loading,
    checkProduct,
    checkNeed,
    checkSector,
    handleSelectedProductOption,
    selectedProductOption,
    selectedProduct,
    setSelectedSector,
    selectedSector,
    selectedNeed,
    needsOptions,
    setSelectedNeed,
    parseForm,
    requestedProducts: products,
    activeProductsOptions,
    setSelectedProductData,
    selectedProductData,
  };

  return useSupportFormsData;
};

export default useSupportForms;
